
import React, { useState } from 'react';
import { AlertCircle, RefreshCw, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CinematicHero from './CinematicHero';
import PersonalityStorySection from './PersonalityStorySection';
import ResultsChat from './ResultsChat';
import SignupHook from './SignupHook';
import ResultsFooter from './ResultsFooter';
import ShareProfileModal from './ShareProfileModal';
import { Button } from '@/components/ui/button';
import { BehavioralFactor, PersonalityProfile as PersonalityProfileType, BehavioralScores } from '@/types/resultsTypes';

interface ResultsContentProps {
  behavioralFactors: BehavioralFactor[];
  objectivityFactor: BehavioralFactor;
  personalityProfile: PersonalityProfileType;
  selectedWordsText: string[];
  selectedWordsCount: number;
  objectivityScore: number;
  scores: BehavioralScores;
  completedAt: string;
  isDualAssessment?: boolean;
  selfBehavioralFactors?: BehavioralFactor[] | null;
  workBehavioralFactors?: BehavioralFactor[] | null;
  selfWordsCount?: number;
  workWordsCount?: number;
  selfSelectedWordsText?: string[];
  workSelectedWordsText?: string[];
  selfPersonalityProfile?: PersonalityProfileType;
  workPersonalityProfile?: PersonalityProfileType;
  userData?: {
    fullName: string;
    email: string;
  };
  companyId?: string | null;
  candidateLanguage?: string;
  lowConfidence?: boolean;
}

const ResultsContent: React.FC<ResultsContentProps> = ({
  behavioralFactors,
  objectivityFactor,
  personalityProfile,
  selectedWordsText,
  selectedWordsCount,
  objectivityScore,
  scores,
  completedAt,
  isDualAssessment = false,
  selfBehavioralFactors = null,
  workBehavioralFactors = null,
  selfWordsCount = 0,
  workWordsCount = 0,
  selfSelectedWordsText = [],
  workSelectedWordsText = [],
  selfPersonalityProfile,
  workPersonalityProfile,
  userData,
  companyId,
  candidateLanguage,
  lowConfidence = false
}) => {
  const navigate = useNavigate();
  const [shareOpen, setShareOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0 overflow-x-hidden">
      {/* Low Confidence Warning Banner */}
      {lowConfidence && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-800 flex-1">
              Quick completion detected. For best accuracy, we recommend spending more time on the assessment.
            </p>
            <button
              onClick={() => navigate('/assessment')}
              className="flex items-center gap-1.5 text-sm font-medium text-amber-700 hover:text-amber-900 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Retake
            </button>
          </div>
        </div>
      )}

      {/* Cinematic Hero Section */}
      <CinematicHero 
        personalityProfile={personalityProfile}
        completedAt={completedAt}
      />

      {/* Share CTA — sticky bar below hero */}
      <div className="sticky top-0 z-20 bg-background/90 backdrop-blur-sm border-b border-border print:hidden">
        <div className="max-w-4xl mx-auto px-4 py-2 flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShareOpen(true)}
            className="flex items-center gap-2 text-sm font-medium"
          >
            <Share2 className="w-4 h-4" />
            Share my profile
          </Button>
        </div>
      </div>

      {/* Main Content Sections with consistent spacing */}
      <div className="space-y-16 w-full">
        {/* Personality Story Section with integrated AI Insights */}
        <PersonalityStorySection
          behavioralFactors={behavioralFactors}
          objectivityFactor={objectivityFactor}
          scores={scores}
          selectedWordsCount={selectedWordsCount}
          isDualAssessment={isDualAssessment}
          selfBehavioralFactors={selfBehavioralFactors}
          workBehavioralFactors={workBehavioralFactors}
          selfWordsCount={selfWordsCount}
          workWordsCount={workWordsCount}
          personalityProfile={personalityProfile}
          selectedWordsText={selectedWordsText}
          selfPersonalityProfile={selfPersonalityProfile}
          workPersonalityProfile={workPersonalityProfile}
          selfSelectedWordsText={selfSelectedWordsText}
          workSelectedWordsText={workSelectedWordsText}
          candidateLanguage={candidateLanguage}
        />

        {/* AI Chat Section */}
        <ResultsChat
          scores={scores}
          isDualAssessment={isDualAssessment}
          selfScores={isDualAssessment && selfBehavioralFactors ? {
            dominance: selfBehavioralFactors.find(f => f.key === 'dominance')?.percentile ?? 50,
            extraversion: selfBehavioralFactors.find(f => f.key === 'extraversion')?.percentile ?? 50,
            patience: selfBehavioralFactors.find(f => f.key === 'patience')?.percentile ?? 50,
            formality: selfBehavioralFactors.find(f => f.key === 'formality')?.percentile ?? 50,
            objectivity: selfBehavioralFactors.find(f => f.key === 'objectivity')?.percentile ?? objectivityFactor.percentile,
          } : null}
          workScores={isDualAssessment && workBehavioralFactors ? {
            dominance: workBehavioralFactors.find(f => f.key === 'dominance')?.percentile ?? 50,
            extraversion: workBehavioralFactors.find(f => f.key === 'extraversion')?.percentile ?? 50,
            patience: workBehavioralFactors.find(f => f.key === 'patience')?.percentile ?? 50,
            formality: workBehavioralFactors.find(f => f.key === 'formality')?.percentile ?? 50,
            objectivity: workBehavioralFactors.find(f => f.key === 'objectivity')?.percentile ?? objectivityFactor.percentile,
          } : null}
          profileType={personalityProfile.type}
        />

        {/* Signup Hook for Anonymous Users */}
        <SignupHook />
      </div>

      {/* Footer */}
      <ResultsFooter completedAt={completedAt} userData={userData} companyId={companyId} />

      {/* Share Profile Modal */}
      <ShareProfileModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        personalityProfile={personalityProfile}
        behavioralFactors={behavioralFactors}
        userName={userData?.fullName}
        shareUrl={window.location.href}
      />
    </div>
  );
};

export default ResultsContent;
